import { Wrapper } from './styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { FormattedMessage } from 'react-intl'
import TicketRow from 'components/shared/Reservation/ticketRows/TicketRow'
import TicketItem from 'components/shared/Reservation/ticketRows/TicketItem'
import SeparatedList from 'components/shared/SeparatedList'
import Loader from 'components/shared/Loader'
import { useEffect, useState } from 'react'
import { useAutoScroll } from 'utils/hooks'
import { DefaultTheme } from 'styled-components'
import { Align } from 'components/shared/Box'

export interface OrderedTicketItem {
    quantity?: number
    date?: string
    text: React.ReactNode
}
interface OrderedTicketProps {
    id: string
    price: number
    items: OrderedTicketItem[]
    onEdit?: () => void
    onDelete?: () => void
    isDisabled?: boolean
    hasCtaDisabled?: boolean
    creator?: JSX.Element | string
    isEdited?: boolean
    setIsEdited?: (value: boolean) => void
    isDeleted?: boolean
    onRevert?: () => void
    editedItem?: 'quantity' | 'date'
    isActionError?: boolean
    withEditEnabled?: boolean
    previewAlign?: Align | [Align, Align]
    theme?: DefaultTheme
    restoreBtnLabel?: React.ReactNode
    shouldKeepBorderInDarkMode?: boolean
    hasUnwrappedDescriptionOnMobile?: boolean
    hasLongerName?: boolean
    testIdPrefix?: string
}

// TODO: needs refactoring and separating into creators and selfcare / too much logic in one place
const OrderedTicket = ({
    id,
    price,
    items,
    onEdit,
    onDelete,
    isDisabled,
    hasCtaDisabled,
    creator,
    isEdited,
    setIsEdited,
    isDeleted,
    onRevert,
    editedItem,
    isActionError,
    withEditEnabled = true,
    previewAlign,
    theme,
    restoreBtnLabel,
    shouldKeepBorderInDarkMode,
    hasUnwrappedDescriptionOnMobile,
    hasLongerName,
    testIdPrefix,
}: OrderedTicketProps) => {
    const { animatePendingScroll, scrollIndex } = useAutoScroll()

    const [isActionInProgress, setIsActionInProgress] = useState(false)

    const hasDarkMode = theme?.mode === 'dark'

    const handleDelete = () => {
        if (!onDelete) return
        setIsActionInProgress(true)
        onDelete()
    }

    const handleRevert = () => {
        if (!onRevert) return
        setIsActionInProgress(true)
        onRevert()
    }

    useEffect(() => {
        setIsActionInProgress(false)
    }, [isDeleted, isActionError])

    useEffect(() => {
        animatePendingScroll(id)
    }, [scrollIndex])

    const handleEdit = () => {
        if (isDisabled) return
        if (creator && setIsEdited) setIsEdited(!isEdited)
        if (onEdit) onEdit()
    }

    return (
        <Wrapper
            isFullWidth
            isEdited={isEdited}
            pl={[16, 40]}
            pr={[16, 40]}
            id={id}
            theme={theme}
            shouldKeepBorderInDarkMode={shouldKeepBorderInDarkMode}
        >
            <SeparatedList
                direction="column"
                gap={[16, 32]}
                borderColor="grayscale_300"
                isFullWidth
            >
                <Box
                    isFullWidth
                    gap={hasLongerName ? [16, 24] : [16, 40]}
                    pt={[16, 32]}
                    pb={creator && isEdited ? 0 : [16, 32]}
                    direction={['column', 'row']}
                    align={previewAlign ?? ['flex-start', 'center']}
                    id={id}
                >
                    <TicketRow
                        price={price}
                        isDisabled={isDisabled || isDeleted}
                        theme={theme}
                        align={previewAlign}
                        hasUnwrappedDescriptionOnMobile={
                            hasUnwrappedDescriptionOnMobile
                        }
                        hasSmallerGap={hasLongerName}
                    >
                        <Box gap={[16, 24]} isFullWidth>
                            {items.map(({ quantity, date, text }, index) => (
                                <TicketItem
                                    quantity={quantity}
                                    date={date}
                                    key={`ordered-ticket-${index}`}
                                    editedItem={editedItem}
                                    isDisabled={isDisabled || isDeleted}
                                    hasSmallerGap={hasLongerName}
                                    theme={theme}
                                >
                                    {text}
                                </TicketItem>
                            ))}
                        </Box>
                    </TicketRow>

                    {(onDelete || withEditEnabled) && (
                        <Box
                            direction={['row-reverse', 'row']}
                            justify={['flex-start', 'flex-end']}
                            gap={16}
                        >
                            {isDeleted ? (
                                <>
                                    {isActionInProgress ? (
                                        <Loader />
                                    ) : (
                                        <Text
                                            as="button"
                                            variant="bodyStrong"
                                            color="secondary_500"
                                            onClick={handleRevert}
                                            hasNoWrap
                                            data-testid={`${
                                                testIdPrefix &&
                                                `${testIdPrefix}-`
                                            }button-restore`}
                                        >
                                            {restoreBtnLabel || (
                                                <FormattedMessage
                                                    id="global.btn.revert_ticket"
                                                    defaultMessage="Przywróć bilet"
                                                />
                                            )}
                                        </Text>
                                    )}
                                </>
                            ) : (
                                <>
                                    {(onEdit || !!creator) &&
                                        !isEdited &&
                                        withEditEnabled && (
                                            <Text
                                                as={
                                                    isDisabled || hasCtaDisabled
                                                        ? 'span'
                                                        : 'button'
                                                }
                                                variant="bodyStrong"
                                                color={
                                                    isDisabled || hasCtaDisabled
                                                        ? 'grayscale_500'
                                                        : 'primary_500'
                                                }
                                                onClick={handleEdit}
                                                data-testid={`${
                                                    testIdPrefix &&
                                                    `${testIdPrefix}-`
                                                }button-edit`}
                                            >
                                                <FormattedMessage
                                                    id="global.btn.edit"
                                                    defaultMessage="Edytuj"
                                                />
                                            </Text>
                                        )}
                                    {onDelete &&
                                        !isEdited &&
                                        (isActionInProgress ? (
                                            <Loader hasNoShrink />
                                        ) : (
                                            <Text
                                                as={
                                                    isDisabled || hasCtaDisabled
                                                        ? 'span'
                                                        : 'button'
                                                }
                                                variant="bodyStrong"
                                                color={(() => {
                                                    if (
                                                        isDisabled ||
                                                        hasCtaDisabled
                                                    )
                                                        return 'grayscale_500'
                                                    else
                                                        return hasDarkMode
                                                            ? 'grayscale_400'
                                                            : 'grayscale_700'
                                                })()}
                                                onClick={() =>
                                                    !isDisabled &&
                                                    !hasCtaDisabled
                                                        ? handleDelete()
                                                        : null
                                                }
                                                data-testid={`${
                                                    testIdPrefix &&
                                                    `${testIdPrefix}-`
                                                }button-delete`}
                                            >
                                                <FormattedMessage
                                                    id="global.btn.remove"
                                                    defaultMessage="Usuń"
                                                />
                                            </Text>
                                        ))}
                                </>
                            )}
                        </Box>
                    )}
                </Box>
                {isEdited && <Box pb={[16, 40]}>{creator}</Box>}
            </SeparatedList>
        </Wrapper>
    )
}

export default OrderedTicket
